<script lang="ts">import { loggedIn, toSiteUrl } from "@infinitro/global";
// Utility for generating site URLs
import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
import DropdownArrow from "./icons/dropdown_arrow.svelte";
import { cfgToThemeCdnPath, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { SearchIcon } from "@infinitro/icons";
import { writable } from "svelte/store";
const dispatch = createEventDispatcher();
export let value = "";
export let placeholder = $t(Verbiage.select_an_option);
export let disabled = false;
export let disabledWithoutStyles = false;
export let icon = null;
export let customClasses = "";
export let inputClasses = "";
export let iconClasses = "";
export let size = "large";
export let options = [];
export let width = "100%";
export let disableMinWidth = false;
export let withIcon = false;
export let customStyles = "";
export let hideValue = false;
export let affectsFlow = false;
export let menuPosition = "bottom";
export let stateToggle = writable(null);
export let isOpen = false;
export let enableSearch = false;
export let searchPlaceholder = $t(Verbiage.search);
let selectedOptionLabel = placeholder;
let selectedOptionIcon = icon;
let selectedOptionIconProps = {};
let containerElement;
let searchQuery = "";
const stateToggleSub = stateToggle.subscribe((info) => {
	if (info === null) {
		return;
	}
	toggleDropdown(info);
});
// Lifecycle: Initialize selected option and set event listener for outside clicks
onMount(() => {
	const selectedOption = options.find((option) => option.value === value);
	if (selectedOption) {
		selectedOptionLabel = selectedOption.label;
		selectedOptionIcon = selectedOption.icon;
		selectedOptionIconProps = selectedOption.iconProps || {};
	}
	document.addEventListener("click", handleClickOutside, true);
	return () => document.removeEventListener("click", handleClickOutside, true);
});
onDestroy(() => {
	stateToggleSub();
});
// Toggles the dropdown open/closed when clicked
function toggleDropdown(event) {
	event.stopPropagation();
	if (!disabled && !disabledWithoutStyles) {
		if (isOpen) {
			searchQuery = "";
		}
		isOpen = !isOpen;
	}
}
// Handles selecting an option from the dropdown
function handleOptionSelect(event, option) {
	event.stopPropagation();
	value = option.value;
	selectedOptionLabel = option.label;
	selectedOptionIcon = option.icon;
	selectedOptionIconProps = option.iconProps || {};
	isOpen = false;
	searchQuery = "";
	dispatch("change", value);
	tick();
}
// Closes the dropdown when clicking outside of it
function handleClickOutside(event) {
	if (!containerElement.contains(event.target)) {
		isOpen = false;
		searchQuery = "";
		tick();
	}
}
$: normalizedSearchQuery = searchQuery.trim().toLowerCase();
$: filteredOptions = enableSearch && normalizedSearchQuery?.length > 0 ? options.filter((option) => {
	const label = option.label?.toLowerCase() ?? "";
	const optionValue = option.value?.toLowerCase() ?? "";
	return label.includes(normalizedSearchQuery) || optionValue.includes(normalizedSearchQuery);
}) : options;
// Reactive statement: Updates the selected option's label and icon whenever `value` or `options` change
$: {
	value;
	options;
	const selectedOption = options.find((option) => option.value === value);
	if (selectedOption) {
		selectedOptionLabel = selectedOption.label;
		selectedOptionIcon = selectedOption.icon;
		selectedOptionIconProps = selectedOption.iconProps || {};
	} else {
		selectedOptionLabel = placeholder;
		selectedOptionIcon = icon;
		selectedOptionIconProps = {};
	}
}
$: useAlternative = $stateUi.dropdown?.dropdownStyle === "alternative";
$: useAlternative2 = $stateUi.dropdown?.dropdownStyle === "alternative2";
$: useAlternative4 = $stateUi.dropdown?.dropdownStyle === "alternative4";
$: useDropdownBackgroundImage = $stateUi.dropdown.useDropdownBackgroundImage;
</script>

<div class="container" bind:this={containerElement} style="--width: {width};">
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div
		class:input-container-alternative={useAlternative || useAlternative4}
		class:input-container-alternative2={useAlternative2}
		class:input-container-alternative4={useAlternative4}
		class:alt2-open={useAlternative2 && isOpen}
		class={`input-container ${size} ${customClasses} ${disabled ? 'input-disabled' : ''} ${disabledWithoutStyles ? 'disabled-without-styles' : ''}  ${isOpen ? 'input-border-hover-active' : ''} ${disableMinWidth ? '' : 'with-min-width'}`}
		on:click={toggleDropdown}
		style="width: {width}; {customStyles}"
	>
		{#if withIcon && typeof selectedOptionIcon === 'string'}
			<div class={`input-icon ${size} ${iconClasses}`}>
				<img src={selectedOptionIcon} alt={$t(Verbiage.icon_alt, 'alts')} />
			</div>
		{:else if withIcon && selectedOptionIcon !== null}
			<div class={`input-icon ${size} ${iconClasses}`}>
				<svelte:component this={selectedOptionIcon} {...selectedOptionIconProps} />
			</div>
		{/if}
		{#if useDropdownBackgroundImage}
			<div class="dropdown-background-image-container">
				<img
					src={cfgToThemeCdnPath('/button/background.png')}
					class="dropdown-background-image"
					alt={$t(Verbiage.illustration_alt, 'alts')}
				/>
			</div>
		{/if}
		<slot name="components"></slot>
		<!-- svelte-ignore a11y-role-has-required-aria-props -->
		<!-- svelte-ignore a11y-aria-activedescendant-has-tabindex -->
		<div
			class={`input ${inputClasses}`}
			class:input-alternative={useAlternative || useAlternative4}
			role="combobox"
			aria-expanded={isOpen}
			aria-disabled={disabled || disabledWithoutStyles}
			aria-activedescendant={value}
		>
			<span class={value ? 'text-white' : 'text-gray-400'}
				>{!hideValue ? selectedOptionLabel : ''}</span
			>
			<div class="input-end">
				<slot name="components-end"></slot>
				{#if !disabledWithoutStyles}
					<DropdownArrow fill={isOpen ? 'white' : '#9CA3AF'} />
				{/if}
			</div>
		</div>
		{#if isOpen && !disabled && !disabledWithoutStyles && !affectsFlow}
			<div
				class:options-list-alternative={useAlternative || useAlternative4}
				class:options-list-alternative2={useAlternative2}
				class:options-list-alternative4={useAlternative4}
				class:menu-position-up={menuPosition === 'top'}
				class="options-list absolute-position {size}"
			>
				{#if enableSearch}
					<div class="search-container" on:click|stopPropagation>
						<div class="search-icon">
							<SearchIcon />
						</div>
						<input
							type="text"
							placeholder={searchPlaceholder}
							bind:value={searchQuery}
							class="search-input"
							on:click|stopPropagation
						/>
					</div>
				{/if}
				{#each filteredOptions as option, index}
					<!-- svelte-ignore a11y-interactive-supports-focus -->
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<div
						class:option-item-alternative={useAlternative || useAlternative4}
						class:option-item-alternative2={useAlternative2}
						class="option-item"
						role="option"
						aria-selected={option.value === value}
						on:click={(e) => handleOptionSelect(e, option)}
						class:selected={option.value === value}
						class:first={index === 0}
						class:last={index === filteredOptions.length - 1}
					>
						{#if withIcon && typeof option.icon === 'string'}
							<img src={option.icon} alt={$t(Verbiage.icon_alt, 'alts')} class="option-icon" />
						{:else if withIcon && option.icon !== null}
							<svelte:component this={option.icon} {...option.iconProps} class="option-icon" />
						{/if}
						<span class={option.icon !== null ? 'option-label' : ''}>{option.label}</span>
					</div>
				{/each}
			</div>
		{/if}
	</div>
	{#if isOpen && !disabled && !disabledWithoutStyles && affectsFlow}
		<div class:options-list-alternative2={useAlternative2} class="options-list flow-content {size}">
			{#if enableSearch}
				<div class="search-container" on:click|stopPropagation>
					<div class="search-icon">
						<SearchIcon />
					</div>
					<input
						type="text"
						placeholder={searchPlaceholder}
						bind:value={searchQuery}
						class="search-input"
						on:click|stopPropagation
					/>
				</div>
			{/if}
			{#each filteredOptions as option, index}
				<a href={toSiteUrl(option.value, $loggedIn)}>
					<div
						class="option-item"
						role="option"
						aria-selected={option.value === value}
						class:selected={option.value === value}
						class:option-item-alternative2={useAlternative2}
						class:first={index === 0}
						class:last={index === filteredOptions.length - 1}
					>
						{#if withIcon && typeof option.icon === 'string'}
							<img src={option.icon} alt={$t(Verbiage.icon_alt, 'alts')} class="option-icon" />
						{:else if withIcon && option.icon !== null}
							<svelte:component this={option.icon} {...option.iconProps} class="option-icon" />
						{/if}
						<span class={option.icon !== null ? 'option-label' : ''}>{option.label}</span>
					</div>
				</a>
			{/each}
		</div>
	{/if}
</div>

<style>
	.container {
		display: flex;
		width: 100%;
		flex-direction: column;
		font-family: var(--font-main);
		width: var(--width);
}
	.input-container {
		position: relative;
		display: flex;
		cursor: pointer;
		align-items: center;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-300);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
}
	.input-container-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-500);
		border-radius: 6px;
		background-image: unset;
}
	.input-container-alternative:not(.disabled-without-styles):hover {
		background-image: unset !important;
	}

	.input-container-alternative2 {
		position: relative;
		border-radius: 8px;
		border-style: none;
		background-image: unset;
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-background-500) 100%
		) !important;
}

	.input-container-alternative2::before {
		position: absolute;
		inset: 0px;
		border-radius: 8px;
		padding: 1px;
		content: '';
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
}

	.input-container-alternative4 {
		border-width: 0px;
}

	.alt2-open::before {
		background: linear-gradient(
			180deg,
			rgba(var(--color-primary-500-rgb) / 0.9) 0%,
			transparent 100%
		);
	}

	.input-container:not(.disabled-without-styles) {
		cursor: pointer;
}

	.input-container:not(.disabled-without-styles):hover {
		border-color: var(--color-secondary-500);
}

	.input-container:not(.disabled-without-styles):active {
		border-color: var(--color-secondary-500);
}

	.input-container:not(.disabled-without-styles):focus-within {
		border-color: var(--color-primary-500);
		outline-color: var(--color-primary-500);
	}
	.input-container:not(.disabled-without-styles):focus-within,
	.input-container:not(.disabled-without-styles):hover,
	.input-container:not(.disabled-without-styles).input-border-hover-active {
		border-color: var(--color-primary-500);
		outline-color: var(--color-primary-500);
	}
	.input-container:not(.disabled-without-styles).input-disabled {
		cursor: not-allowed;
		opacity: 0.25;
		background: var(--gradient-alternative-4);
}
	.with-min-width {
		min-width: 120px;
}
	.input-icon {
		margin-right: -12px;
		margin-left: 8px;
		display: flex;
		height: 100%;
		width: 30px;
		align-items: center;
		justify-content: center;
}
	.input-icon.extra-large {
		width: 45px;
}
	.input-icon img {
		height: 20px;
}
	.input-icon.small img {
		height: 16px;
}
	.input {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		text-wrap: nowrap;
		border-radius: 16px;
		background-color: transparent;
		padding-right: 8px;
		padding-left: 15px;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
}

	.input-alternative {
		border-radius: 12px;
}

	.input:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	.input-end {
		display: flex;
		align-items: center;
		gap: 0.5rem;
}
	.search-container {
		position: relative;
		margin-bottom: 0.5rem;
		margin-top: 0.5rem;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 0.5rem;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
}
	.search-icon {
		position: absolute;
		left: 1.25rem;
		display: flex;
		align-items: center;
}
	.search-input {
		width: 100%;
		border-radius: 8px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--component-input-background);
		padding: 0.25rem;
		padding-left: 2.5rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.search-input:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
		--tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
		--tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
		box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
		--tw-ring-color: var(--color-secondary-500);
}
	.options-list {
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-500);
		--tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		/* Scrollbar styles */
		&::-webkit-scrollbar {
				width: 0.375rem;
		}
		&::-webkit-scrollbar-track {
				border-radius: 16px;
		}
		&::-webkit-scrollbar-track {
				background-color: var(--color-background-100);
		}
		&::-webkit-scrollbar-track {
				padding: 0.25rem;
		}
		&::-webkit-scrollbar-thumb {
				border-radius: 16px;
		}
		&::-webkit-scrollbar-thumb {
				background-color: var(--color-neutral-900);
		}
		&::-webkit-scrollbar-thumb {
				--tw-shadow-color: var(--shadow-neutral);
				--tw-shadow: var(--tw-shadow-colored);
		}
		&::-webkit-scrollbar-thumb:hover {
				background-color: var(--color-neutral-300);
		}
		z-index: 42 !important;
}
	.options-list-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-100);
		border-radius: 12px;
		background-image: unset;
}
	.options-list-alternative2 {
		border-radius: 8px;
		border-color: var(--color-neutral-300);
		--tw-backdrop-blur: blur(100px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		background: var(--color-background-500);
}
	.options-list-alternative4 {
		border-width: 0px;
}
	.absolute-position {
		position: absolute;
		z-index: 10;
		max-height: 200px;
		width: 100%;
		overflow-y: auto;
}
	.flow-content {
		margin-top: 0.25rem;
		width: 100%;
		overflow-y: auto;
}
	.option-item {
		display: flex;
		cursor: pointer;
		align-items: center;
		text-wrap: nowrap;
		border-bottom-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		color: var(--color-text-300);
}
	.option-item-alternative:hover,
	.option-item-alternative.selected {
		background-image: unset !important;
	}
	.option-item:hover {
		background-color: var(--color-background-100);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.option-item.selected {
		background-color: var(--color-background-100);
}
	.options-list > .option-item:last-child {
		border-bottom-width: 0px;
}

	.options-list > a:last-child .option-item {
		border-bottom-width: 0px;
}
	.option-item-alternative2 {
		border-bottom-width: 1px;
		border-color: var(--color-neutral-500);
}
	.option-icon {
		margin-right: 0.5rem;
		height: 20px;
}
	.option-label {
		margin-left: 0.25rem;
		font-size: 12px;
}
	.input-container.extra-large {
		height: 70px;
		font-size: 14px;
}
	/* Size classes */
	.input-container.large {
		height: 48px;
		font-size: 14px;
}
	.input-container.medium {
		height: 40px;
		font-size: 14px;
}
	.input-container.small {
		height: 36px;
		font-size: 12px;
}
	.options-list.extra-large {
		top: 74px;
}
	.options-list.large {
		top: 52px;
}
	.options-list.medium {
		top: 44px;
}
	.options-list.small {
		top: 40px;
}
	.menu-position-up.extra-large {
		top: -126px;
}
	.menu-position-up.large {
		top: -148px;
}
	.menu-position-up.medium {
		top: -156px;
}
	.menu-position-up.small {
		top: -160px;
}

	.dropdown-background-image-container {
		position: absolute;
		inset: 0px;
		height: 100%;
		width: 100%;
		overflow: hidden;
		border-radius: inherit;
}

	.dropdown-background-image {
		position: absolute;
		top: 0px;
		max-width: 100px;
		opacity: 0.2;
		left: 80%;
		transform: translateX(-50%) translateY(-50%);
		width: 100%;
		height: auto;
		-o-object-fit: cover;
		   object-fit: cover;
}
</style>
